import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import { useI18next } from 'gatsby-plugin-react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { setLocale } from 'yup';
import { fr } from 'date-fns/locale/fr';
import { de } from 'date-fns/locale/de';
import { es } from 'date-fns/locale/es';
import { pt } from 'date-fns/locale/pt';
import { it } from 'date-fns/locale/it';
import { enUS } from 'date-fns/locale/en-US'; // Default/fallback

export const useOnClickOutside = (ref, handler) => {
    useEffect(
        () => {
            const listener = (event) => {
                // Do nothing if clicking ref's element or descendent elements
                if (!ref.current || ref.current.contains(event.target)) {
                    return;
                }

                handler(event);
            };
            document.addEventListener('mousedown', listener);
            document.addEventListener('touchstart', listener);
            return () => {
                document.removeEventListener('mousedown', listener);
                document.removeEventListener('touchstart', listener);
            };
        },
        // Add ref and handler to effect dependencies
        // It's worth noting that because passed in handler is a new ...
        // ... function on every render that will cause this effect ...
        // ... callback/cleanup to run every render. It's not a big deal ...
        // ... but to optimize you can wrap handler in useCallback before ...
        // ... passing it into this hook.
        [ ref, handler ]
    );
};

export const useHover = () => {
    const [ value, setValue ] = useState(false);
    const ref = useRef(null);
    const handleMouseOver = () => setValue(true);
    const handleMouseOut = () => setValue(false);
    useEffect(
        () => {
            const node = ref.current;
            if (node) {
                node.addEventListener('mouseover', handleMouseOver);
                node.addEventListener('mouseout', handleMouseOut);
                return () => {
                    node.removeEventListener('mouseover', handleMouseOver);
                    node.removeEventListener('mouseout', handleMouseOut);
                };
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ ref.current ] // Recall only if ref changes
    );
    return [ ref, value ];
};

export const useSiteMetadata = () => {
    const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          twitterUsername
          image
          siteUrl
        }
      }
    }
  `);

    return data.site.siteMetadata;
};

export const useIsMounted = () => {
    const isMounted = useRef(false);
    useEffect(() => {
        isMounted.current = true;
        return () => isMounted.current = false;
    }, []);
    return useCallback(() => isMounted.current, []);
};

// IMPORTANT: call this before configuring formik yup validationSchema in useFormik();
export const useTranslateFormErrors = (() => {
    const { i18n, t } = useI18next();

    const localizeYup = () => {
        setLocale({
            mixed: {
                default: t('yup.mixed.default'),
                required: t('yup.mixed.required')
            },
            number: {
                min: t('yup.number.min')
            },
            string: {
                email: t('yup.string.email'),
                min: t('yup.string.min'),
                max: t('yup.string.max')
            }
        });
    };

    useEffect(() => {
        i18n.on('languageChanged', localizeYup);
        i18n.on('initialized', localizeYup);

        return () => {
            i18n.off('languageChanged', lng => {
            });
        };
    }, [ i18n, t ]);

    localizeYup();
});

export const useJourneyLink = () => {
    const location = useLocation();
    const curPath = location.pathname.replace(/^(\/(it|en|es|pt|de|fr))?(.+)$/, '$3');
    const JOURNEY = [
        '/platform/data-extraction',
        '/platform/archive-document-management',
        '/platform/overview-business-intelligence/archive-document-management',
        '/platform/aida-gpt',
        '/platform/security-and-certifications',
        '/platform/aida-pay',
        '/platform/relations-knowledge-graph',
        '/platform/integrations',
        '/platform/aida-mobile',
        '/platform/aida-xerox-app'
    ];

    const i = JOURNEY.indexOf(curPath);

    let link = JOURNEY[ 0 ];
    if (i >= 0) {
        // Dynamic journey
        let next = i + 1;
        if (next > JOURNEY.length - 1) {
            next = 0;
        }

        link = JOURNEY[ next ];
    }

    return link;
};

const locales = {
    fr,
    de,
    es,
    pt,
    it,
    'en-US': enUS, // You might get 'en-US' from i18next
    en: enUS      // Or just 'en'
    // Add other language codes as needed, mapping to the imported locale
};

export const useDateFnsLocale = () => {
    const { language } = useI18next();

    // Handle potential variations in language codes (e.g., 'en' vs 'en-US')
    const getLocaleForLanguage = (lang) => {
        if (locales[ lang ]) {
            return locales[ lang ];
        }
        // Try to match the base language if a regional variant isn't found
        const baseLang = lang.split('-')[ 0 ];
        if (locales[ baseLang ]) {
            return locales[ baseLang ];
        }
        return locales[ 'en-US' ]; // Fallback to a default (e.g., English)
    };

    return getLocaleForLanguage(language);
};
